import axios from 'axios';
import {
  CREATE_MANUAL_VERIFICATION_FORM,
  GET_MANUAL_VERIFICATION_FORMS,
  GET_ONBOARDING_FORMS_API,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  first_defined,
  get_response_items,
  normalize_manual_form,
  normalize_onboarding_form,
  parse_manual_verification_response,
} from './manual_verification.helpers';

export function get_manual_verification_error_message(error, fallback_message) {
  const error_message = error?.response?.data?.message || error?.message || fallback_message;
  return error_message;
}

export default {
  data: () => ({
    loading_forms: false,
    verification_forms: [],
    form_name: '',
    form_description: '',
    create_form_running: false,
    create_form_is_active: true,
    onboarding_forms: [],
    loading_onboarding_forms: false,
  }),
  methods: {
    first_non_null(...values) {
      const first_value = first_defined(...values);
      return first_value;
    },

    parse_json_if_string(value) {
      if (typeof value !== 'string') {
        return value;
      }
      try {
        const parsed_value = JSON.parse(value);
        return parsed_value;
      } catch {
        return value;
      }
    },

    parse_response_data(response) {
      const parsed_response = parse_manual_verification_response(response, this.parse_json_if_string);
      return parsed_response;
    },

    format_created_at(value) {
      if (!value) {
        const empty_value = 'â€”';
        return empty_value;
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        const raw_value = String(value);
        return raw_value;
      }

      try {
        const formatted_value = new Intl.DateTimeFormat(undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }).format(date);
        return formatted_value;
      } catch {
        const locale_value = date.toLocaleString();
        return locale_value;
      }
    },

    async fetch_verification_forms() {
      this.loading_forms = true;
      try {
        const response = await axios.get(GET_MANUAL_VERIFICATION_FORMS, get_request_config());
        const parsed = this.parse_response_data(response);
        const items = get_response_items(parsed, 'data');
        this.verification_forms = items.map(normalize_manual_form).filter(Boolean);
      } catch (error) {
        this.verification_forms = [];
        this.show_error(get_manual_verification_error_message(error, 'Failed to load manual verification forms.'));
      } finally {
        this.loading_forms = false;
      }
    },

    open_create_form_modal() {
      this.form_name = '';
      this.form_description = '';
      this.create_form_is_active = true;
    },

    get_manual_verification_form_id(form) {
      const form_id = form ? this.first_non_null(form.manual_verification_form_id, form.form_id, form.id) : null;
      return form_id;
    },

    async create_form() {
      if (this.create_form_running) {
        return;
      }

      this.create_form_running = true;
      const loader = await this.show_loader('Creating form...');
      try {
        const form_name = String(this.form_name || '').trim();
        if (!form_name) {
          this.show_error('Form name is required.');
          return;
        }

        const payload = {
          form_name,
          form_description: String(this.form_description || '').trim(),
          is_active: Boolean(this.create_form_is_active),
        };
        const response = await axios.post(CREATE_MANUAL_VERIFICATION_FORM, payload, get_request_config());
        const parsed = this.parse_response_data(response);
        const was_successful = parsed?.status === 'success';

        if (was_successful) {
          const created = normalize_manual_form(parsed?.data || null);
          if (created) {
            this.verification_forms = [created, ...(Array.isArray(this.verification_forms) ? this.verification_forms : [])];
          }
          this.show_success(parsed?.message || 'Manual verification form created successfully.');
          this.close_modal('create_verification_form');
          this.form_name = '';
          this.form_description = '';
          this.create_form_is_active = true;
        } else {
          this.show_error(parsed?.message || 'Failed to create manual verification form.');
        }
      } catch (error) {
        this.show_error(get_manual_verification_error_message(error, 'Failed to create manual verification form.'));
      } finally {
        await this.close_loader(loader);
        this.create_form_running = false;
      }
    },

    async fetch_onboarding_forms() {
      this.loading_onboarding_forms = true;
      try {
        const response = await axios.get(GET_ONBOARDING_FORMS_API, get_request_config());
        const parsed = this.parse_response_data(response);
        const items = get_response_items(parsed, 'data', 'results');
        this.onboarding_forms = items.map(normalize_onboarding_form).filter(Boolean);
      } catch (error) {
        this.onboarding_forms = [];
        this.show_error(get_manual_verification_error_message(error, 'Failed to load onboarding forms.'));
      } finally {
        this.loading_onboarding_forms = false;
      }
    },
  },
};